import { forwardRef, InputHTMLAttributes, useState } from "react";
import cn from "classnames";
import TextControl, { TextControlProps } from "@ebsco-ui/text-control";
import Button from "@ebsco-ui/button";
import { createBemBlockBuilder } from "@ebsco-ui/utils";

import "./password-input.scss";

export interface PasswordInputProps
  extends Omit<TextControlProps, "description" | "children">,
    Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  hidePasswordButtonText: string;
  showPasswordButtonText: string;
  descriptionId?: string;
}

const baseClassName = "eb-password-input";

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      id,
      className,
      label,
      error,
      disabled,
      hidePasswordButtonText,
      showPasswordButtonText,
      descriptionId,
      ...rest
    }: PasswordInputProps,
    ref,
  ) => {
    const [isHidden, setIsHidden] = useState(true);
    const toggleHidden = () => setIsHidden(!isHidden);

    const getBlocksWith = createBemBlockBuilder([className, baseClassName]);

    const buttonMessage = isHidden
      ? showPasswordButtonText
      : hidePasswordButtonText;

    return (
      <TextControl
        className={getBlocksWith()}
        label={label}
        error={error}
        labelButton={
          <Button
            onClick={toggleHidden}
            styleType="link"
            className={getBlocksWith("__button")}
          >
            {buttonMessage}
          </Button>
        }
      >
        <input
          type={isHidden ? "password" : "text"}
          id={id}
          disabled={disabled}
          className={getBlocksWith("__input")}
          aria-invalid={Boolean(error)}
          aria-describedby={cn(error && `${id}-error`, descriptionId)}
          ref={ref}
          {...rest}
        />
      </TextControl>
    );
  },
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
